// src/pages/manager/ManagerPayment.jsx

import React, { useState, useEffect, useMemo } from 'react';
import {
    FaWallet,
    FaCheckCircle,
    FaClock,
    FaExclamationTriangle,
    FaCalculator,
    FaMoneyBillWave,
    FaLandmark,
    FaInfoCircle,
    FaCloudUploadAlt,
    FaReceipt,
    FaUsers,
    FaUserPlus
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../api/axios';

// ======================================================================
// HELPER
// ======================================================================
const normalizeText = (value) => String(value || '').toLowerCase().trim();

const safeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const firstValue = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null && value !== '') return value;
    }

    return 0;
};

const formatRupiah = (value) => {
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
    }).format(safeNumber(value));
};

const formatDate = (dateValue) => {
    if (!dateValue) return 'Tanggal tidak valid';

    const dateObj = new Date(dateValue);

    if (Number.isNaN(dateObj.getTime())) {
        return 'Tanggal tidak valid';
    }

    return dateObj.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};

const getEntryMembers = (athlete) => {
    if (!athlete) return [];

    if (Array.isArray(athlete.entry_members)) {
        return athlete.entry_members
            .map(member => String(member || '').trim())
            .filter(Boolean);
    }

    if (typeof athlete.entry_members === 'string' && athlete.entry_members.trim() !== '') {
        try {
            const parsed = JSON.parse(athlete.entry_members);

            if (Array.isArray(parsed)) {
                return parsed
                    .map(member => String(member || '').trim())
                    .filter(Boolean);
            }
        } catch (error) {
            return athlete.entry_members
                .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                .map(member => String(member || '').trim())
                .filter(Boolean);
        }
    }

    return [];
};

const getDisplayName = (athlete) => {
    const members = getEntryMembers(athlete);

    if (members.length > 1) {
        return members.join(' / ');
    }

    return athlete?.name || '-';
};

const getPoomsaeCategoryLabel = (category) => {
    const value = normalizeText(category);

    if (value.includes('freestyle') || value.includes('free style')) return 'Freestyle';
    if (value.includes('pair') || value.includes('pasangan') || value.includes('ganda')) return 'Pair';
    if (value.includes('team') || value.includes('regu') || value.includes('kelompok')) return 'Team';
    if (value.includes('individual') || value.includes('individu')) return 'Individual';

    return category || 'Individual';
};

const getPriceKey = (athlete) => {
    const competitionType = normalizeText(athlete?.competition_type);

    if (competitionType === 'kyorugi') {
        return 'kyorugi';
    }

    if (competitionType === 'poomsae') {
        const category = normalizeText(athlete?.poomsae_category || 'Individual');

        if (category.includes('freestyle') || category.includes('free style')) return 'poomsae_freestyle';
        if (category.includes('pair') || category.includes('pasangan') || category.includes('ganda')) return 'poomsae_pair';
        if (category.includes('team') || category.includes('regu') || category.includes('kelompok')) return 'poomsae_team';

        return 'poomsae_individual';
    }

    return 'unknown';
};

const getBillingLabel = (key) => {
    const labels = {
        team_registration: 'Biaya Pendaftaran Kontingen',
        kyorugi: 'Kyorugi',
        poomsae_individual: 'Poomsae Individual',
        poomsae_freestyle: 'Poomsae Freestyle',
        poomsae_pair: 'Poomsae Pair',
        poomsae_team: 'Poomsae Team',
        unknown: 'Lainnya',
        other: 'Lainnya'
    };

    return labels[key] || key || 'Lainnya';
};

const getClassLabel = (athlete) => {
    if (!athlete) return '-';

    if (athlete.competition_type === 'Kyorugi') {
        const belt = athlete.belt ? ` • Sabuk ${athlete.belt}` : '';
        return `${athlete.weight_class || '-'}${belt}`;
    }

    return getPoomsaeCategoryLabel(athlete.poomsae_category);
};

const getTypeLabel = (athlete) => {
    const type = normalizeText(athlete?.type);

    if (type === 'prestasi') return 'Prestasi';
    if (type === 'festival') return 'Festival';

    return athlete?.type || '-';
};

const normalizeServerBreakdown = (rawBreakdown = []) => {
    if (!Array.isArray(rawBreakdown)) return [];

    return rawBreakdown
        .map((item) => {
            const key = item.key || item.type || item.code || 'unknown';
            const count = safeNumber(firstValue(item.qty, item.count, item.quantity));
            const total = safeNumber(firstValue(item.amount, item.total, item.subtotal));
            const price = safeNumber(firstValue(item.unit_price, item.price, count > 0 ? total / count : 0));

            return {
                key,
                label: item.label || getBillingLabel(key),
                count,
                price,
                total,
                entries: []
            };
        })
        .filter(item => item.count > 0 || item.total > 0);
};

const ManagerPayment = () => {
    // ======================================================================
    // STATE
    // ======================================================================
    const [athletes, setAthletes] = useState([]);
    const [settings, setSettings] = useState(null);
    const [latestPayment, setLatestPayment] = useState(null);
    const [totalVerifiedAmount, setTotalVerifiedAmount] = useState(0);
    const [serverBillingSummary, setServerBillingSummary] = useState(null);
    const [history, setHistory] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);

    const [proofFile, setProofFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    // ======================================================================
    // FETCH DATA
    // ======================================================================
    const fetchData = async () => {
        setIsLoading(true);

        try {
            const [athRes, setRes, payRes, histRes] = await Promise.all([
                api.get('/athletes/my-athletes'),
                api.get('/settings'),
                api.get('/payments/my-payment'),
                api.get('/payments/history')
            ]);

            const athleteData = athRes.data.result || athRes.data.data || athRes.data || [];
            const settingData = setRes.data.result || setRes.data.data || setRes.data || {};
            const paymentData = payRes.data || {};
            const historyData = histRes.data.result || histRes.data.data || histRes.data || [];

            const backendSummary = paymentData.billing_summary || paymentData.billingSummary || {};

            setAthletes(Array.isArray(athleteData) ? athleteData : []);
            setSettings(settingData);

            setLatestPayment(paymentData.latest_payment || null);

            setTotalVerifiedAmount(
                safeNumber(firstValue(
                    backendSummary.total_verified_amount,
                    backendSummary.totalVerifiedAmount,
                    paymentData.total_verified_amount,
                    paymentData.totalVerifiedAmount
                ))
            );

            setServerBillingSummary({
                team_registration_fee: safeNumber(firstValue(
                    backendSummary.team_registration_fee,
                    backendSummary.teamRegistrationFee,
                    paymentData.team_registration_fee,
                    paymentData.teamRegistrationFee
                )),
                total_athlete_amount: safeNumber(firstValue(
                    backendSummary.total_athlete_amount,
                    backendSummary.totalAthleteAmount,
                    paymentData.total_athlete_amount,
                    paymentData.totalAthleteAmount
                )),
                total_registered_amount: safeNumber(firstValue(
                    backendSummary.total_registered_amount,
                    backendSummary.totalRegisteredAmount,
                    backendSummary.total_bill_amount,
                    backendSummary.totalBillAmount,
                    paymentData.total_registered_amount,
                    paymentData.totalRegisteredAmount,
                    paymentData.total_bill_amount,
                    paymentData.totalBillAmount
                )),
                outstanding_amount: safeNumber(firstValue(
                    backendSummary.outstanding_amount,
                    backendSummary.outstandingAmount,
                    paymentData.outstanding_amount,
                    paymentData.outstandingAmount
                )),
                unpaid_amount_by_athlete: safeNumber(firstValue(
                    backendSummary.unpaid_amount_by_athlete,
                    backendSummary.unpaidAmountByAthlete,
                    paymentData.unpaid_amount_by_athlete,
                    paymentData.unpaidAmountByAthlete
                )),
                total_athletes: safeNumber(firstValue(
                    backendSummary.total_athletes,
                    backendSummary.totalAthletes,
                    paymentData.total_athletes,
                    paymentData.totalAthletes
                )),
                verified_athletes: safeNumber(firstValue(
                    backendSummary.verified_athletes,
                    backendSummary.verifiedAthletes,
                    paymentData.verified_athletes,
                    paymentData.verifiedAthletes
                )),
                pending_athletes: safeNumber(firstValue(
                    backendSummary.pending_athletes,
                    backendSummary.pendingAthletes,
                    paymentData.pending_athletes,
                    paymentData.pendingAthletes
                )),
                billing_breakdown:
                    backendSummary.billing_breakdown ||
                    backendSummary.billingBreakdown ||
                    paymentData.billing_breakdown ||
                    paymentData.billingBreakdown ||
                    []
            });

            setHistory(Array.isArray(historyData) ? historyData : []);
        } catch (error) {
            console.error('Gagal load data pembayaran:', error);
            toast.error('Gagal memuat data tagihan.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();

        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ======================================================================
    // SETTINGS & PERHITUNGAN TAGIHAN
    // ======================================================================
    const settingConfig = settings?.config || settings || {};
    const prices = settingConfig?.prices || {};
    const bankInfo = settingConfig?.bank_info || {};

    const billingData = useMemo(() => {
        const breakdownMap = {
            team_registration: {
                label: 'Biaya Pendaftaran Kontingen',
                count: 0,
                total: 0,
                price: safeNumber(prices.team_registration),
                entries: []
            },
            kyorugi: {
                label: 'Kyorugi',
                count: 0,
                total: 0,
                price: safeNumber(prices.kyorugi),
                entries: []
            },
            poomsae_individual: {
                label: 'Poomsae Individual',
                count: 0,
                total: 0,
                price: safeNumber(prices.poomsae_individual),
                entries: []
            },
            poomsae_freestyle: {
                label: 'Poomsae Freestyle',
                count: 0,
                total: 0,
                price: safeNumber(prices.poomsae_freestyle || prices.poomsae_individual),
                entries: []
            },
            poomsae_pair: {
                label: 'Poomsae Pair',
                count: 0,
                total: 0,
                price: safeNumber(prices.poomsae_pair),
                entries: []
            },
            poomsae_team: {
                label: 'Poomsae Team',
                count: 0,
                total: 0,
                price: safeNumber(prices.poomsae_team),
                entries: []
            },
            unknown: {
                label: 'Lainnya',
                count: 0,
                total: 0,
                price: 0,
                entries: []
            }
        };

        let calculatedGrandTotal = 0;

        const teamRegistrationPrice = safeNumber(prices.team_registration);

        if (athletes.length > 0 && teamRegistrationPrice > 0) {
            breakdownMap.team_registration.count = 1;
            breakdownMap.team_registration.total = teamRegistrationPrice;
            breakdownMap.team_registration.price = teamRegistrationPrice;
            calculatedGrandTotal += teamRegistrationPrice;
        }

        athletes.forEach(athlete => {
            const priceKey = getPriceKey(athlete);
            const item = breakdownMap[priceKey] || breakdownMap.unknown;

            const storedFee = safeNumber(athlete.fee_amount);
            const price = storedFee > 0 ? storedFee : item.price;

            item.count += 1;
            item.total += price;
            item.entries.push(athlete);

            calculatedGrandTotal += price;
        });

        return {
            grandTotal: calculatedGrandTotal,
            breakdown: breakdownMap
        };
    }, [athletes, prices]);

    const serverBreakdown = useMemo(() => {
        return normalizeServerBreakdown(serverBillingSummary?.billing_breakdown || []);
    }, [serverBillingSummary]);

    const visibleBreakdown = useMemo(() => {
        if (serverBreakdown.length > 0) return serverBreakdown;

        return Object.keys(billingData.breakdown)
            .map(key => ({
                key,
                ...billingData.breakdown[key]
            }))
            .filter(item => item && item.count > 0);
    }, [serverBreakdown, billingData]);

    const serverGrandTotal = safeNumber(firstValue(
        serverBillingSummary?.total_registered_amount,
        serverBillingSummary?.total_bill_amount
    ));

    const grandTotal = serverGrandTotal > 0
        ? serverGrandTotal
        : billingData.grandTotal;

    const verifiedAmount = safeNumber(firstValue(
        serverBillingSummary?.total_verified_amount,
        totalVerifiedAmount
    ));

    const serverOutstanding = safeNumber(serverBillingSummary?.outstanding_amount);

    const billToPay = serverBillingSummary?.outstanding_amount !== undefined && serverBillingSummary?.outstanding_amount !== null
        ? Math.max(serverOutstanding, 0)
        : Math.max(grandTotal - verifiedAmount, 0);

    const teamRegistrationFee = safeNumber(firstValue(
        serverBillingSummary?.team_registration_fee,
        prices.team_registration
    ));

    const pendingEntryCount = serverBillingSummary?.pending_athletes !== undefined
        ? safeNumber(serverBillingSummary.pending_athletes)
        : athletes.filter(athlete => athlete.payment_status !== 'Verified').length;

    const isPending = latestPayment?.status === 'Pending' && billToPay > 0;
    const isRejected = latestPayment?.status === 'Rejected' && billToPay > 0;
    const isFullyPaid = billToPay <= 0 && grandTotal > 0;

    const paymentHistory = useMemo(() => {
        return history.filter(item => item.type === 'payment');
    }, [history]);

    const timelineHistory = useMemo(() => {
        return history.slice(0, 12);
    }, [history]);

    // ======================================================================
    // UPLOAD HANDLER
    // ======================================================================
    const handleFileChange = (event) => {
        const file = event.target.files[0];

        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            return toast.warning('Ukuran file maksimal 2MB!');
        }

        if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
            return toast.warning('Format file harus JPG, JPEG, atau PNG.');
        }

        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }

        setProofFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!proofFile) {
            return toast.warning('Pilih file bukti transfer terlebih dahulu!');
        }

        if (billToPay <= 0) {
            return toast.info('Tidak ada tagihan yang perlu dibayar.');
        }

        setIsUploading(true);

        const formData = new FormData();
        formData.append('proof_image', proofFile);

        // Backend sekarang menghitung ulang outstanding_amount.
        // Field ini tetap dikirim untuk kompatibilitas lama.
        formData.append('total_amount', billToPay);

        try {
            await api.post('/payments/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success('Bukti pembayaran berhasil dikirim!');

            setProofFile(null);

            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }

            setPreviewUrl(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.msg || 'Gagal mengirim bukti pembayaran.');
        } finally {
            setIsUploading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary"></div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0 animate-fade-in">
            {/* HEADER */}
            <div className="mb-4">
                <h4 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                    <FaWallet className="text-primary" /> Tagihan & Pembayaran
                </h4>
                <p className="text-muted small mb-0">
                    Selesaikan administrasi agar atlet dapat ditimbang dan bertanding.
                </p>
            </div>

            {/* RINGKASAN KARTU ATAS */}
            <div className="row g-3 mb-4">
                <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white">
                        <div className="card-body p-4">
                            <p className="text-muted small fw-bold mb-1 text-uppercase">
                                Total Biaya Pendaftaran
                            </p>
                            <h3 className="fw-black text-dark m-0">
                                {formatRupiah(grandTotal)}
                            </h3>
                            <div className="small text-muted fw-bold mt-1">
                                {athletes.length} entry
                                {teamRegistrationFee > 0 ? ' + biaya kontingen' : ''}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white">
                        <div className="card-body p-4">
                            <p className="text-muted small fw-bold mb-1 text-uppercase">
                                Sudah Diverifikasi
                            </p>
                            <h3 className="fw-black text-success m-0">
                                {formatRupiah(verifiedAmount)}
                            </h3>
                            <div className="small text-muted fw-bold mt-1">
                                Pembayaran lunas/verified
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-primary text-white position-relative overflow-hidden">
                        <div className="position-absolute end-0 top-0 opacity-10 p-3">
                            <FaMoneyBillWave size={80} />
                        </div>
                        <div className="card-body p-4 position-relative z-1">
                            <p className="text-white-50 small fw-bold mb-1 text-uppercase">
                                Sisa Tagihan Anda
                            </p>
                            <h3 className="fw-black text-white m-0">
                                {formatRupiah(billToPay)}
                            </h3>
                            <div className="small text-white-50 fw-bold mt-1">
                                {isFullyPaid ? 'Sudah lunas' : 'Perlu diselesaikan'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                {/* KOLOM KIRI: STATUS & FORM UPLOAD */}
                <div className="col-lg-7">
                    {isRejected && (
                        <div className="alert alert-danger border-0 shadow-sm rounded-4 mb-4 d-flex gap-3">
                            <FaExclamationTriangle className="fs-3 mt-1 flex-shrink-0" />
                            <div>
                                <h6 className="fw-bold mb-1">Pembayaran Ditolak!</h6>
                                <p className="small mb-0 opacity-75">
                                    Panitia menolak bukti transfer sebelumnya. Catatan:{' '}
                                    <b>{latestPayment?.admin_note || 'Bukti tidak valid.'}</b>
                                </p>
                            </div>
                        </div>
                    )}

                    {isFullyPaid ? (
                        <div className="card border-0 shadow-sm rounded-4 bg-success bg-opacity-10 mb-4 text-center p-5">
                            <FaCheckCircle size={60} className="text-success mb-3 mx-auto" />
                            <h5 className="fw-bold text-success mb-1">Semua Tagihan Lunas!</h5>
                            <p className="small text-muted mb-0">
                                Seluruh entry Anda telah terverifikasi dan siap untuk proses pertandingan.
                            </p>
                        </div>
                    ) : isPending ? (
                        <div className="card border-0 shadow-sm rounded-4 bg-warning bg-opacity-10 mb-4 text-center p-5">
                            <FaClock size={60} className="text-warning mb-3 mx-auto" />
                            <h5 className="fw-bold text-dark mb-1">Sedang Diverifikasi</h5>
                            <p className="small text-muted mb-2">
                                Bukti transfer Anda sedang dicek oleh Panitia Keuangan.
                            </p>
                            <div className="badge bg-white text-dark border rounded-pill px-3 py-2">
                                Nominal dikirim: {formatRupiah(latestPayment?.total_amount)}
                            </div>
                        </div>
                    ) : billToPay > 0 ? (
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-header bg-white p-4 border-bottom d-flex align-items-center gap-2">
                                <FaLandmark className="text-primary" />
                                <h6 className="fw-bold m-0">Instruksi Pembayaran</h6>
                            </div>

                            <div className="card-body p-4 p-md-5">
                                <div className="bg-light p-4 rounded-4 mb-4 border text-center">
                                    <p className="text-muted small fw-bold mb-2">
                                        Transfer tepat sebesar{' '}
                                        <span className="text-dark">{formatRupiah(billToPay)}</span>{' '}
                                        ke rekening berikut:
                                    </p>

                                    <h4 className="fw-black text-primary mb-1">
                                        {bankInfo.bank_name || 'Nama Bank Belum Diatur'}
                                    </h4>

                                    <h3 className="fw-bold text-dark mb-1 tracking-widest">
                                        {bankInfo.account_number || '-'}
                                    </h3>

                                    <p className="text-muted small fw-medium m-0">
                                        a.n. {bankInfo.account_holder || '-'}
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    <label className="form-label small fw-bold text-muted mb-3">
                                        Upload Bukti Transfer
                                    </label>

                                    <label className="d-block border-dashed rounded-4 p-4 text-center bg-light mb-4 position-relative overflow-hidden">
                                        {previewUrl ? (
                                            <>
                                                <img
                                                    src={previewUrl}
                                                    alt="Preview"
                                                    className="img-fluid rounded-3 mb-3 shadow-sm"
                                                    style={{ maxHeight: '200px' }}
                                                />
                                                <div className="text-primary small fw-bold">
                                                    Klik untuk mengganti gambar
                                                </div>
                                            </>
                                        ) : (
                                            <div className="py-4 text-muted">
                                                <FaCloudUploadAlt size={40} className="mb-2 text-primary opacity-75" />
                                                <h6 className="fw-bold text-dark mb-1">Pilih atau Tarik File</h6>
                                                <small>Mendukung JPG, JPEG, PNG (Max 2MB)</small>
                                            </div>
                                        )}

                                        <input
                                            type="file"
                                            className="d-none"
                                            accept="image/jpeg,image/png,image/jpg"
                                            onChange={handleFileChange}
                                        />
                                    </label>

                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100 py-3 rounded-pill fw-bold shadow-sm d-flex justify-content-center align-items-center gap-2"
                                        disabled={isUploading}
                                    >
                                        {isUploading ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm"></div>
                                                Memproses...
                                            </>
                                        ) : (
                                            <>
                                                <FaCheckCircle /> Kirim Bukti Pembayaran
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    ) : (
                        <div className="alert alert-info border-0 shadow-sm rounded-4 d-flex gap-2">
                            <FaInfoCircle className="mt-1" />
                            <span className="small">
                                Silakan daftarkan atlet atau entry terlebih dahulu untuk memunculkan tagihan pembayaran.
                            </span>
                        </div>
                    )}

                    {/* RIWAYAT AKTIVITAS */}
                    {timelineHistory.length > 0 && (
                        <div className="card border-0 shadow-sm rounded-4 bg-white">
                            <div className="card-body p-4">
                                <h6 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
                                    <FaReceipt className="text-muted" /> Riwayat Aktivitas
                                </h6>

                                <div
                                    className="custom-scrollbar pe-2"
                                    style={{ maxHeight: '420px', overflowY: 'auto' }}
                                >
                                    <div className="d-flex flex-column gap-3">
                                        {timelineHistory.map((item, index) => {
                                            const data = item.data || {};
                                            const isPayment = item.type === 'payment';
                                            const members = getEntryMembers(data);

                                            return (
                                                <div
                                                    key={`${item.type}-${data.uuid || data.id || index}`}
                                                    className="bg-light p-3 rounded-3 border hover-up-small"
                                                >
                                                    <div className="d-flex align-items-start gap-3">
                                                        <div
                                                            className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${
                                                                isPayment
                                                                    ? 'bg-success bg-opacity-10 text-success'
                                                                    : 'bg-primary bg-opacity-10 text-primary'
                                                            }`}
                                                            style={{ width: '42px', height: '42px' }}
                                                        >
                                                            {isPayment ? <FaReceipt /> : <FaUserPlus />}
                                                        </div>

                                                        <div className="flex-grow-1 overflow-hidden">
                                                            <div className="d-flex justify-content-between gap-2 mb-1">
                                                                <div className="fw-bold text-dark text-truncate">
                                                                    {isPayment ? 'Pembayaran Tagihan' : getDisplayName(data)}
                                                                </div>
                                                                <small className="text-muted flex-shrink-0">
                                                                    {formatDate(item.date)}
                                                                </small>
                                                            </div>

                                                            {isPayment ? (
                                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                    <span className="fw-black text-dark">
                                                                        {formatRupiah(data.total_amount)}
                                                                    </span>

                                                                    {data.status === 'Verified' && (
                                                                        <span className="badge bg-success bg-opacity-10 text-success border border-success rounded-pill">
                                                                            Lunas
                                                                        </span>
                                                                    )}

                                                                    {data.status === 'Pending' && (
                                                                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning rounded-pill">
                                                                            Menunggu
                                                                        </span>
                                                                    )}

                                                                    {data.status === 'Rejected' && (
                                                                        <span className="badge bg-danger bg-opacity-10 text-danger border border-danger rounded-pill">
                                                                            Ditolak
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                    <span className="badge bg-white text-dark border rounded-pill">
                                                                        {data.competition_type || '-'}
                                                                    </span>

                                                                    <span className="badge bg-white text-dark border rounded-pill">
                                                                        {getTypeLabel(data)}
                                                                    </span>

                                                                    <span className="badge bg-white text-dark border rounded-pill">
                                                                        {data.division || '-'} / {getClassLabel(data)}
                                                                    </span>

                                                                    {members.length > 1 && (
                                                                        <span className="badge bg-info bg-opacity-10 text-info border border-info rounded-pill">
                                                                            {members.length} Nama / 1 Entry
                                                                        </span>
                                                                    )}

                                                                    <span
                                                                        className={`badge rounded-pill border ${
                                                                            item.status === 'Unpaid'
                                                                                ? 'bg-danger bg-opacity-10 text-danger border-danger'
                                                                                : 'bg-success bg-opacity-10 text-success border-success'
                                                                        }`}
                                                                    >
                                                                        {item.status === 'Unpaid' ? 'Belum Bayar' : 'Tercover / Lunas'}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* KOLOM KANAN: RINCIAN & RIWAYAT TRANSAKSI */}
                <div className="col-lg-5">
                    {/* RINCIAN BIAYA */}
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-header bg-white p-4 border-bottom">
                            <h6 className="fw-bold m-0 d-flex align-items-center gap-2">
                                <FaCalculator className="text-primary" /> Rincian Pendaftaran
                            </h6>
                        </div>

                        <div className="card-body p-0">
                            {grandTotal === 0 ? (
                                <div className="p-4 text-center text-muted small fst-italic">
                                    Belum ada entry yang didaftarkan.
                                </div>
                            ) : (
                                <ul className="list-group list-group-flush">
                                    {visibleBreakdown.map(item => {
                                        if (!item || item.count === 0) return null;

                                        const isTeamRegistration = item.key === 'team_registration';

                                        return (
                                            <li key={item.key} className="list-group-item p-4 border-0 border-bottom">
                                                <div className="d-flex justify-content-between align-items-start gap-3">
                                                    <div>
                                                        <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                            {isTeamRegistration && <FaUsers className="text-primary" />}
                                                            {item.label}
                                                        </h6>

                                                        <small className="text-muted">
                                                            {isTeamRegistration ? '1 Kontingen' : `${item.count} Entry`}
                                                            {item.price > 0 && (
                                                                <> &times; {formatRupiah(item.price)}</>
                                                            )}
                                                        </small>
                                                    </div>

                                                    <span className="fw-bold text-dark">
                                                        {formatRupiah(item.total)}
                                                    </span>
                                                </div>

                                                {item.entries && item.entries.length > 0 && (
                                                    <div className="mt-3 d-flex flex-column gap-2">
                                                        {item.entries.slice(0, 3).map(entry => {
                                                            const members = getEntryMembers(entry);

                                                            return (
                                                                <div
                                                                    key={entry.uuid || entry.id}
                                                                    className="small bg-light rounded-3 px-3 py-2 d-flex justify-content-between gap-2"
                                                                >
                                                                    <span className="text-muted text-truncate">
                                                                        {getDisplayName(entry)}
                                                                    </span>

                                                                    {members.length > 1 && (
                                                                        <span className="badge bg-info bg-opacity-10 text-info border border-info rounded-pill flex-shrink-0">
                                                                            {members.length} Nama
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}

                                                        {item.entries.length > 3 && (
                                                            <div className="small text-muted fw-bold px-1">
                                                                +{item.entries.length - 3} entry lainnya
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {isTeamRegistration && (
                                                    <div className="mt-3 small bg-primary bg-opacity-10 text-primary rounded-3 px-3 py-2">
                                                        Biaya ini dihitung satu kali untuk kontingen/tim, tidak dikalikan jumlah atlet.
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}

                                    <li className="list-group-item p-4 bg-light">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="fw-bold text-muted text-uppercase small">
                                                Subtotal
                                            </span>
                                            <h5 className="fw-black text-primary m-0">
                                                {formatRupiah(grandTotal)}
                                            </h5>
                                        </div>
                                    </li>

                                    <li className="list-group-item p-4 bg-white">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="fw-bold text-muted text-uppercase small">
                                                Sudah Diverifikasi
                                            </span>
                                            <h6 className="fw-black text-success m-0">
                                                - {formatRupiah(verifiedAmount)}
                                            </h6>
                                        </div>
                                    </li>

                                    <li className="list-group-item p-4 bg-primary bg-opacity-10">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="fw-bold text-primary text-uppercase small">
                                                Sisa Tagihan
                                            </span>
                                            <h5 className="fw-black text-primary m-0">
                                                {formatRupiah(billToPay)}
                                            </h5>
                                        </div>
                                    </li>
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* INFO ENTRY */}
                    <div className="card border-0 shadow-sm rounded-4 mb-4 bg-white">
                        <div className="card-body p-4">
                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                <FaUsers className="text-primary" /> Ringkasan Entry
                            </h6>

                            <div className="row g-2">
                                <div className="col-6">
                                    <div className="bg-light rounded-3 p-3">
                                        <div className="small text-muted fw-bold">Total Entry</div>
                                        <h5 className="fw-black text-dark m-0">{athletes.length}</h5>
                                    </div>
                                </div>

                                <div className="col-6">
                                    <div className="bg-light rounded-3 p-3">
                                        <div className="small text-muted fw-bold">Belum Lunas</div>
                                        <h5 className="fw-black text-danger m-0">
                                            {pendingEntryCount}
                                        </h5>
                                    </div>
                                </div>
                            </div>

                            <div className="small text-muted mt-3">
                                Pair dan Team Poomsae dihitung sebagai <b>1 entry</b> sesuai data pendaftaran.
                                {teamRegistrationFee > 0 && (
                                    <>
                                        {' '}Biaya kontingen dihitung <b>1 kali per tim</b>.
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* RIWAYAT TRANSAKSI */}
                    {paymentHistory.length > 0 && (
                        <div className="card border-0 shadow-sm rounded-4 bg-light">
                            <div className="card-body p-4">
                                <h6 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
                                    <FaReceipt className="text-muted" /> Riwayat Transaksi
                                </h6>

                                <div
                                    className="custom-scrollbar pe-2"
                                    style={{ maxHeight: '450px', overflowY: 'auto' }}
                                >
                                    <div className="d-flex flex-column gap-3">
                                        {paymentHistory.map((item, index) => {
                                            const paymentData = item.data || {};
                                            const amount = safeNumber(paymentData.total_amount);

                                            return (
                                                <div
                                                    key={paymentData.uuid || paymentData.id || index}
                                                    className="bg-white p-3 rounded-3 shadow-sm border border-white hover-up-small"
                                                >
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <span className="small text-muted fw-bold">
                                                            {formatDate(item.date)}
                                                        </span>

                                                        {paymentData.status === 'Verified' && (
                                                            <span className="badge bg-success-subtle text-success border border-success rounded-pill px-2" style={{ fontSize: '0.7rem' }}>
                                                                Lunas
                                                            </span>
                                                        )}

                                                        {paymentData.status === 'Pending' && (
                                                            <span className="badge bg-warning-subtle text-warning border border-warning rounded-pill px-2" style={{ fontSize: '0.7rem' }}>
                                                                Menunggu
                                                            </span>
                                                        )}

                                                        {paymentData.status === 'Rejected' && (
                                                            <span className="badge bg-danger-subtle text-danger border border-danger rounded-pill px-2" style={{ fontSize: '0.7rem' }}>
                                                                Ditolak
                                                            </span>
                                                        )}
                                                    </div>

                                                    <h6 className="fw-bold text-dark m-0">
                                                        {formatRupiah(amount)}
                                                    </h6>

                                                    {paymentData.admin_note && (
                                                        <div className="small text-muted mt-2">
                                                            Catatan: {paymentData.admin_note}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* CSS STYLE */}
            <style>{`
                .animate-fade-in {
                    animation: fadeIn 0.4s ease-out forwards;
                }

                .fw-black {
                    font-weight: 900;
                }

                .tracking-widest {
                    letter-spacing: 2px;
                }

                .border-dashed {
                    border: 2px dashed #dee2e6;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .border-dashed:hover {
                    border-color: #0d6efd;
                    background-color: #e9ecef !important;
                }

                .custom-scrollbar::-webkit-scrollbar {
                    width: 5px;
                }

                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }

                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #dee2e6;
                    border-radius: 10px;
                }

                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #adb5bd;
                }

                .hover-up-small {
                    transition: transform 0.2s;
                }

                .hover-up-small:hover {
                    transform: translateY(-2px);
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default ManagerPayment;